import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore, ProtectedRoute, PublicOnlyRoute } from './auth';
import { ErrorBoundary } from './components/ErrorBoundary';
import { MainLayout } from './components/layout/MainLayout';
import {
  LoginPage,
  DashboardPage,
  ClientsListPage,
  ClientNewPage,
  ClientDetailPage,
  ClientFormPage,
  ProjectsListPage,
  ProjectFormPage,
  ProjectDetailPage,
  CatalogPage,
  CatalogVersionsPage,
  CatalogEditorPage,
  UsersListPage,
  UserFormPage,
  UnauthorizedPage,
  SettingsPage,
} from './pages';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

function AppRoutes() {
  const { checkAuth, isLoading } = useAuthStore();
  
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Caricamento...</p>
        </div>
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />
      
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      
      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        
        {/* Clients */}
        <Route path="clients" element={<ClientsListPage />} />
        <Route path="clients/new" element={<ClientNewPage />} />
        <Route path="clients/:id" element={<ClientDetailPage />} />
        <Route path="clients/:id/edit" element={<ClientFormPage />} />
        
        {/* Projects */}
        <Route path="projects" element={<ProjectsListPage />} />
        <Route path="projects/new" element={<ProjectFormPage />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        
        {/* Catalog */}
        <Route path="catalog" element={<CatalogPage />} />

        {/* Catalog Versions List - Admin only */}
        <Route
          path="catalog/versions"
          element={
            <ProtectedRoute requiredRole="admin">
              <CatalogVersionsPage />
            </ProtectedRoute>
          }
        />

        {/* Catalog Editor - Admin only */}
        <Route
          path="catalog/versions/:version"
          element={
            <ProtectedRoute requiredRole="admin">
              <CatalogEditorPage />
            </ProtectedRoute>
          }
        />

        {/* Users - Admin only */}
        <Route
          path="users"
          element={
            <ProtectedRoute requiredRole="admin">
              <UsersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/new"
          element={
            <ProtectedRoute requiredRole="admin">
              <UserFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/:id/edit"
          element={
            <ProtectedRoute requiredRole="admin">
              <UserFormPage />
            </ProtectedRoute>
          }
        />
        
        {/* Settings - Admin only */}
        <Route
          path="settings"
          element={
            <ProtectedRoute requiredRole="admin">
              <SettingsPage />
            </ProtectedRoute>
          }
        />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
